import { useState } from 'react';
import { Card, Title, Text, Flex, Badge, ProgressBar } from '@tremor/react';
import type { ClassifierErrorsResponse, ClassifierErrorDocument } from '../types';

interface ClassifierErrorsPanelProps {
  data: ClassifierErrorsResponse | null;
}

// Error type colors and labels
const ERROR_TYPE_CONFIG = {
  rateLimit: { color: 'text-orange-400', bg: 'bg-orange-400/20', label: 'Rate Limit' },
  timeout: { color: 'text-yellow-400', bg: 'bg-yellow-400/20', label: 'Timeout' },
  apiError: { color: 'text-red-400', bg: 'bg-red-400/20', label: 'API Error' },
  unknown: { color: 'text-gray-400', bg: 'bg-gray-400/20', label: 'Unknown' },
} as const;

function ErrorTypeBadge({ type, count }: { type: keyof typeof ERROR_TYPE_CONFIG; count: number }) {
  if (count === 0) return null;
  const config = ERROR_TYPE_CONFIG[type];
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs ${config.bg} ${config.color}`}>
      {config.label}: {count}
    </span>
  );
}

function DocumentErrorCard({ doc, isExpanded, onToggle }: {
  doc: ClassifierErrorDocument;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return date.toLocaleDateString();
  };

  const errorRate = doc.totalGroups > 0
    ? ((doc.erroredGroupCount || doc.errorCount) / doc.totalGroups) * 100
    : 0;

  return (
    <div className="bg-gray-900/50 rounded-lg border border-gray-700 overflow-hidden">
      {/* Header - Always visible */}
      <button
        onClick={onToggle}
        className="w-full p-3 text-left hover:bg-gray-800/50 transition-colors"
      >
        <Flex justifyContent="between" alignItems="start">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <span className="text-gray-400 text-sm flex-shrink-0">
              {isExpanded ? '▼' : '▶'}
            </span>
            <Text className="text-white font-mono text-sm truncate">
              {doc.documentId}
            </Text>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Badge color={doc.failedDueToErrors ? 'red' : 'yellow'} size="xs">
              {doc.status}
            </Badge>
            <Text className="text-gray-500 text-xs">
              {formatDate(doc.updatedAt)}
            </Text>
          </div>
        </Flex>

        {/* Error summary bar */}
        <div className="mt-2">
          <Flex justifyContent="between" className="mb-1">
            <span className="text-xs text-gray-400">
              <span className="text-red-400 font-medium">
                {doc.erroredGroupCount || doc.errorCount}
              </span>
              {' / '}{doc.totalGroups} groups errored ({errorRate.toFixed(0)}%)
            </span>
            {doc.harmfulCount > 0 && (
              <span className="text-xs text-yellow-400">
                {doc.harmfulCount} harmful found
              </span>
            )}
          </Flex>
          <ProgressBar
            value={100 - errorRate}
            color="emerald"
            className="h-1.5"
          />
        </div>

        {/* Error type badges */}
        {doc.errorTypes && (
          <div className="mt-2 flex flex-wrap gap-1">
            <ErrorTypeBadge type="rateLimit" count={doc.errorTypes.rateLimit} />
            <ErrorTypeBadge type="timeout" count={doc.errorTypes.timeout} />
            <ErrorTypeBadge type="apiError" count={doc.errorTypes.apiError} />
            <ErrorTypeBadge type="unknown" count={doc.errorTypes.unknown} />
          </div>
        )}
      </button>

      {/* Expanded details */}
      {isExpanded && doc.erroredGroups && doc.erroredGroups.length > 0 && (
        <div className="border-t border-gray-700 p-3 space-y-3 bg-gray-950/50">
          <Text className="text-gray-400 text-xs font-medium uppercase tracking-wide">
            Failed Sentence Groups
          </Text>

          {doc.erroredGroups.map((group, idx) => (
            <div key={idx} className="bg-gray-900/80 rounded p-3 space-y-2">
              {/* Group header */}
              <Flex justifyContent="between" alignItems="center">
                <Text className="text-gray-500 text-xs">
                  Group #{group.groupIndex}
                  {group.sentenceIndices && group.sentenceIndices.length > 0 && (
                    <span className="ml-2">
                      (sentences {group.sentenceIndices.join(', ')})
                    </span>
                  )}
                </Text>
              </Flex>

              {/* The text that failed */}
              <div className="bg-gray-800/50 rounded p-2 border-l-2 border-red-500/50">
                <Text className="text-gray-300 text-sm leading-relaxed">
                  "{group.text}"
                </Text>
              </div>

              {/* Errors */}
              <div className="space-y-1">
                {group.errors.map((err, errIdx) => (
                  <div key={errIdx} className="flex items-start gap-2">
                    <span className={`text-xs px-1.5 py-0.5 rounded ${ERROR_TYPE_CONFIG[err.errorType].bg} ${ERROR_TYPE_CONFIG[err.errorType].color}`}>
                      {err.classifier}
                    </span>
                    <Text className="text-red-300/80 text-xs flex-1">
                      {err.error}
                    </Text>
                  </div>
                ))}
              </div>

              {/* Successful classifiers (partial data) */}
              {group.successfulClassifiers && group.successfulClassifiers.length > 0 && (
                <div className="pt-2 border-t border-gray-700/50">
                  <Text className="text-gray-500 text-xs mb-1">
                    Partial results (classifiers that succeeded):
                  </Text>
                  <div className="flex flex-wrap gap-1">
                    {group.successfulClassifiers.map((sc, scIdx) => (
                      <span key={scIdx} className="text-xs px-1.5 py-0.5 rounded bg-green-400/10 text-green-400">
                        {sc.classifier}: {sc.label}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}

          {doc.erroredGroupCount > (doc.erroredGroups?.length || 0) && (
            <Text className="text-gray-500 text-xs text-center">
              ... and {doc.erroredGroupCount - (doc.erroredGroups?.length || 0)} more errored groups
            </Text>
          )}
        </div>
      )}
    </div>
  );
}

export function ClassifierErrorsPanel({ data }: ClassifierErrorsPanelProps) {
  const [expandedDocs, setExpandedDocs] = useState<Set<string>>(new Set());

  if (!data || data.documents.length === 0) {
    return null; // Don't show panel if no errors
  }

  const toggleExpanded = (docId: string) => {
    setExpandedDocs(prev => {
      const next = new Set(prev);
      if (next.has(docId)) {
        next.delete(docId);
      } else {
        next.add(docId);
      }
      return next;
    });
  };

  const expandAll = () => {
    setExpandedDocs(new Set(data.documents.map(d => d.documentId)));
  };

  const collapseAll = () => {
    setExpandedDocs(new Set());
  };

  // Calculate summary stats
  const summary = data.summary || {
    errorTypes: { rateLimit: 0, timeout: 0, apiError: 0, unknown: 0 },
    classifierErrors: {},
  };

  const totalErrors = Object.values(summary.errorTypes).reduce((a, b) => a + b, 0);

  return (
    <Card className="bg-red-900/20 border border-red-700/50">
      {/* Header */}
      <Flex justifyContent="between" alignItems="start" className="mb-4">
        <div className="flex items-center gap-2">
          <span className="text-red-400 text-lg">⚠</span>
          <Title className="text-white">Classifier Errors</Title>
          <Badge color="red" size="sm">{data.totalCount} docs</Badge>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={expandAll}
            className="text-xs text-gray-400 hover:text-white transition-colors"
          >
            Expand All
          </button>
          <span className="text-gray-600">|</span>
          <button
            onClick={collapseAll}
            className="text-xs text-gray-400 hover:text-white transition-colors"
          >
            Collapse
          </button>
        </div>
      </Flex>

      {/* Summary stats */}
      {totalErrors > 0 && (
        <div className="mb-4 p-3 bg-gray-900/50 rounded-lg border border-gray-700">
          <Text className="text-gray-400 text-xs font-medium mb-2">Error Breakdown</Text>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {Object.entries(summary.errorTypes).map(([type, count]) => {
              const config = ERROR_TYPE_CONFIG[type as keyof typeof ERROR_TYPE_CONFIG];
              const percentage = totalErrors > 0 ? (count / totalErrors) * 100 : 0;
              return (
                <div key={type} className={`p-2 rounded ${config.bg}`}>
                  <Text className={`text-xs ${config.color}`}>{config.label}</Text>
                  <Text className="text-white text-lg font-bold">{count}</Text>
                  <Text className="text-gray-500 text-xs">{percentage.toFixed(0)}%</Text>
                </div>
              );
            })}
          </div>

          {/* Classifier breakdown */}
          {Object.keys(summary.classifierErrors).length > 0 && (
            <div className="mt-3 pt-3 border-t border-gray-700">
              <Text className="text-gray-400 text-xs mb-2">By Classifier</Text>
              <div className="flex flex-wrap gap-1">
                {Object.entries(summary.classifierErrors)
                  .sort(([, a], [, b]) => b - a)
                  .map(([classifier, count]) => (
                    <span key={classifier} className="text-xs px-2 py-1 rounded bg-red-400/10 text-red-300">
                      {classifier}: {count}
                    </span>
                  ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Document list */}
      <div className="space-y-2">
        {data.documents.slice(0, 10).map((doc) => (
          <DocumentErrorCard
            key={doc.documentId}
            doc={doc}
            isExpanded={expandedDocs.has(doc.documentId)}
            onToggle={() => toggleExpanded(doc.documentId)}
          />
        ))}

        {data.totalCount > 10 && (
          <Text className="text-gray-400 text-xs text-center pt-2">
            ... and {data.totalCount - 10} more documents with errors
          </Text>
        )}
      </div>

      {/* Footer */}
      <div className="mt-4 pt-3 border-t border-gray-700">
        <Text className="text-yellow-400/80 text-xs">
          These documents have incomplete classification data. Retry them after tuning rate limits.
          {summary.errorTypes.rateLimit > 0 && (
            <span className="block mt-1 text-gray-400">
              Tip: High rate limit errors suggest reducing OPENAI_RATE_LIMIT_CONCURRENT or CLASSIFIER_CONCURRENT_GROUPS.
            </span>
          )}
          {summary.errorTypes.timeout > 0 && (
            <span className="block mt-1 text-gray-400">
              Tip: Timeout errors may indicate network issues or OpenAI service degradation.
            </span>
          )}
        </Text>
      </div>
    </Card>
  );
}
